import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from "react";

import { Widget } from "@/types";
import { getFileUrl } from "@/lib/utils";

// Hooks & Stores
import { useChatStore } from "@/features/chat";
import { useToast } from "@/features/toast";
import { useCurrentChatId } from "@/hooks";
// ✅ Voice chat artık feature-based store ile yönetiliyor
// 🚧 Voice chat geçici olarak devre dışı
// import { useMicrophone } from "@/hooks/use-microphone";
// import { useVoiceChatStore } from "@/features/voice-chat";
import { useAIModelStore } from "@/features/ai-model-selection";
import { useLLMPrefetch, useLLMDialog } from "@/features/llm-selection";
import { useAIModelPrefetch } from "@/features/ai-model-selection";
import { useFirstMessageHandler } from ".";
import { useFilePicker } from "@/features/file-picker";
import type { ChatAttachment } from "../types";
import { CHAT_MAX_ATTACHMENTS } from "../constants";

export interface ChatActionsState {
  isPromptModalOpen: boolean;
  promptData: {
    text: string;
    model: Widget;
  } | null;
}

export const useChatInput = () => {
  const { t } = useTranslation();
  const { error } = useToast();

  // States - Message ve selectedAttachments artık chat store'da
  const {
    message,
    setMessage,
    selectedAttachments,
    setSelectedAttachments,
    removeSelectedAttachment,
    clearSelectedAttachments,
  } = useChatStore();
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 🚫 fileInputRef removed - file picker kullanılıyor

  // 🆕 File Picker Integration - All file types
  const { openPicker } = useFilePicker();

  // 🚀 MEMORY CLEANUP: Cleanup refs
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Debounced message for safer state management

  // Store
  const { searchQuery, setSearchQuery, isLoading } = useChatStore();
  const activeChat = useCurrentChatId();

  // 🎯 LLM Prefetch for smart loading
  const { prefetchLLMModels } = useLLMPrefetch();

  // 🎯 AI Model Prefetch for smart loading
  const { prefetchAIModels } = useAIModelPrefetch();

  // Smart prefetch logic: User yazmaya başladığında LLM'leri ve AI modellerini prefetch et
  const handleMessageChange = (value: string) => {
    setMessage(value);

    // User ilk kez yazmaya başladığında prefetch
    if (value.length === 1 || value.trim().length > 3) {
      prefetchLLMModels();
      prefetchAIModels();
    }
  };

  // Enhanced hooks for API submission
  const { handleFirstMessage } = useFirstMessageHandler();

  // Search functionality moved to new search feature
  const searchResults: number[] = [];
  const currentResult = 0;
  const handleNextResult = () => {}; // Placeholder
  const handlePrevResult = () => {}; // Placeholder

  // ✅ Voice chat artık store-based
  // 🚧 Voice chat geçici olarak devre dışı
  // const { startVoiceChat, isMobile, transcript } = useVoiceChatStore();
  // const { isAvailable, isChecking } = useMicrophone();
  const isMobile = false; // Geçici olarak false
  // const transcript = ""; // Geçici olarak boş string
  const { openModal } = useAIModelStore();
  const { isOpen: isLLMDialogOpen } = useLLMDialog();

  // Voice chat transcript effect - 🚧 Geçici olarak devre dışı
  // useEffect(() => {
  //   if (transcript) {
  //     setMessage(transcript);
  //   }
  // }, [transcript]);

  // 🚀 MEMORY CLEANUP: Component unmount cleanup
  useEffect(() => {
    return () => {
      // Clear any pending timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Abort any pending API calls
      if (abortControllerRef.current) {
        abortControllerRef.current.abort("Component unmounted");
      }

      // Clear selected attachments to free memory
      clearSelectedAttachments();

      // Clear message state
      setMessage("");
    };
  }, []);

  // 🚀 MEMORY CLEANUP: Active chat change cleanup
  useEffect(() => {
    return () => {
      // When active chat changes, cancel any pending operations
      if (abortControllerRef.current) {
        abortControllerRef.current.abort("Chat changed");
        abortControllerRef.current = null;
      }
    };
  }, [activeChat]);

  // Keyboard shortcuts effect with cleanup
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (isSearchExpanded) {
          handleSearchClose();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    // 🚀 CLEANUP: Remove event listener
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSearchExpanded]);

  // Form submit handler with improved error handling
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // 🎯 LLM dialog açıkken form submit'i engelle
    if (isLLMDialogOpen) {
      return;
    }

    // 🔒 Race protection - eğer halihazırda bir işlem yapılıyorsa engelle
    if (isLoading || isSubmitting) {
      return;
    }

    // Debounced message kullan - daha güvenli state management
    const trimmedMessage = message.trim();

    // 🆕 Validation: Mesaj veya dosya en az biri olmalı
    if (!trimmedMessage && selectedAttachments.length === 0) {
      return;
    }

    // 🆕 Eğer sadece dosya varsa, boş mesaj yerine placeholder gönder
    const messageToSend =
      trimmedMessage ||
      (selectedAttachments.length > 0 ? t("chat.fileAttachedMessage") : "");

    // 🚀 ABORT CONTROLLER: Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    // Submission lock aktive et
    setIsSubmitting(true);

    try {
      // 🆕 Landing Page Akışı: Sadece conversation oluştur ve navigate et
      // Streaming request chat sayfası yüklendiğinde başlatılacak
      const firstMessageResult = await handleFirstMessage(
        messageToSend,
        selectedAttachments.length > 0 ? selectedAttachments : undefined,
      );

      if (!firstMessageResult.success) {
        throw new Error(
          firstMessageResult.error?.message || "İlk mesaj işlemi başarısız",
        );
      }

      // Navigation başarılı, form'u reset et
      // Chat sayfası mount olduğunda streaming otomatik başlayacak
      setMessage("");
      clearSelectedAttachments();
    } catch (errorCatch) {
      console.error("Submit error:", errorCatch);
      error(
        errorCatch instanceof Error ? errorCatch.message : t("error.unknown"),
        {
          title: t("error.title"),
          actions: [
            { label: t("common.retry"), action: () => handleSubmit(e as any) },
          ],
        },
      );
    } finally {
      // Her durumda submission lock'u kaldır
      setIsSubmitting(false);
      // Clear abort controller
      abortControllerRef.current = null;
    }
  };

  // 🆕 File Picker Handler - All file types support
  const handleFilePickerOpen = () => {
    openPicker({
      maxSelection: CHAT_MAX_ATTACHMENTS,
      allowedTypes: [
        "image/*",
        "video/*",
        "audio/*",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ],
      title: t("chat.selectFiles", "Dosya Seç"),
      description: t(
        "chat.selectFilesDescription",
        "Görsel, video, ses veya belge ekleyin",
      ),
      onSelect: (pickedFiles) => {
        // Picker tüm kaynaklarda dosyayı seçim anında R2'ye yükler;
        // burada sadece ChatAttachment'a map'liyoruz (sahte File hack'i yok).
        const attachments: ChatAttachment[] = [];
        for (const file of pickedFiles) {
          const fullUrl = getFileUrl({ fileUrl: file.url });
          if (!fullUrl) {
            // URL'siz dosya backend'e taşınamaz; sessizce drop etmek yerine logla
            console.warn("Attachment URL'siz geldi, atlanıyor:", file.name);
            continue;
          }
          attachments.push({
            id: file.id,
            url: fullUrl,
            name: file.name,
            mimeType: file.type,
            size: file.size,
          });
        }

        // 🆕 Mevcut eklere ekle, üzerine yazma!
        setSelectedAttachments([...selectedAttachments, ...attachments]);
      },
      onCancel: () => {
        // File picker cancelled
      },
    });
  };

  const removeAttachment = (index: number) => {
    removeSelectedAttachment(index);
  };

  // Search handling
  const handleSearchToggle = () => {
    setIsSearchExpanded(!isSearchExpanded);
    if (!isSearchExpanded) {
      setSearchQuery("");
    }
  };

  const handleSearchClose = () => {
    setIsSearchExpanded(false);
    setSearchQuery("");
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Voice chat handling - 🚧 Geçici olarak devre dışı
  const handleVoiceChatClick = (_e: React.MouseEvent) => {
    // 🚧 Voice chat geçici olarak devre dışı bırakıldı
    return;

    // if (!message.trim()) {
    //   e.preventDefault();
    //   if (isMobile) {
    //     error(t("error.voiceChatNotSupported"), {
    //       title: t("error.title"),
    //     });
    //   } else if (!isAvailable && !isChecking) {
    //     error(t("error.microphoneNotAvailable"), {
    //       title: t("error.title"),
    //     });
    //   } else {
    //     startVoiceChat();
    //   }
    // }
  };

  // AI Model modal opening without callback - let selectModel handle wizard opening
  const handleAIModelOpen = () => {
    openModal(); // Callback olmadan aç, wizard normal açılsın
  };

  return {
    // Form state
    message,
    setMessage: handleMessageChange, // 🎯 Smart message setter with prefetch
    selectedAttachments,
    setSelectedAttachments, // Expose setter for external control
    isSearchExpanded,

    // Search state
    searchQuery,
    searchResults,
    currentResult,

    // Loading states
    isLoading,
    isSubmitting,
    // 🚧 Voice chat geçici olarak devre dışı
    // isAvailable,
    // isChecking,
    isMobile,

    // Actions
    handleSubmit,
    removeAttachment,
    handleSearchToggle,
    handleSearchClose,
    handleSearchChange,
    handleVoiceChatClick,
    handleAIModelOpen,
    handleNextResult,
    handlePrevResult,
    // 🆕 File Picker Actions
    handleFilePickerOpen,
  };
};
